/**
 * Career Referential Page (Admin RH)
 * 
 * Gestion du référentiel métiers : familles, métiers, niveaux de séniorité.
 * Phase 1 du module Career & Skills Intelligence.
 */
import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Briefcase, Layers, TrendingUp, Plus, Edit, Trash2,
  Sparkles, AlertCircle, CheckCircle2, Award, Users,
  ChevronRight, Tag, ShieldCheck, Loader2,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const FAMILY_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#0ea5e9', '#8b5cf6', '#ef4444', '#14b8a6'];

// ─────────────────────────────────────────────────────────────
// Data Health Panel — diagnostic + consolidation
// ─────────────────────────────────────────────────────────────
function DataHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null); // 'consolidate' | 'dedup'

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/career/data-health');
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de diagnostic');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const runConsolidate = async () => {
    if (!window.confirm(
      "Migrer les anciennes fiches de poste de la collection legacy vers la collection canonique ?\n\n"
      + "Les fiches déjà présentes (même nom) seront ignorées. Aucune donnée n'est supprimée."
    )) return;
    try {
      setRunning('consolidate');
      const res = await api.post('/career/data-health/consolidate-profiles');
      toast.success(`${res.data.migrated} fiche(s) migrée(s)`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setRunning(null);
    }
  };

  const runDedup = async () => {
    if (!window.confirm(
      "Supprimer les doublons (même nom) dans les fiches de poste ?\n\n"
      + "La plus ancienne entrée est conservée. Les employés rattachés aux doublons "
      + "seront automatiquement réassignés vers la fiche conservée. Action irréversible."
    )) return;
    try {
      setRunning('dedup');
      const res = await api.post('/career/data-health/deduplicate-profiles');
      toast.success(
        `${res.data.deleted} doublon(s) supprimé(s)`
        + (res.data.reassignedEmployees > 0 ? ` · ${res.data.reassignedEmployees} employé(s) réassigné(s)` : '')
      );
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setRunning(null);
    }
  };

  if (loading) {
    return <p className="text-sm text-slate-400 text-center py-8">Diagnostic en cours...</p>;
  }
  if (!data) return null;

  const { jobProfiles, skills, employees, evaluations, referential } = data;
  const legacyMigrationNeeded = (jobProfiles.legacyOnly || []).length > 0;
  const duplicatesPresent = (jobProfiles.duplicates || []).length > 0;
  const orphanSkills = (skills.orphanSkillIds || []).length;

  const Tile = ({ label, value, hint, color = 'slate', testId }) => (
    <div className={`p-3 rounded-lg bg-${color}-50 border border-${color}-200`} data-testid={testId}>
      <p className={`text-xs text-${color}-700`}>{label}</p>
      <p className={`text-2xl font-bold text-${color}-900 mt-0.5`}>{value}</p>
      {hint && <p className={`text-xs text-${color}-600/80 mt-1`}>{hint}</p>}
    </div>
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-600" />
                Cohérence des données
              </CardTitle>
              <CardDescription>
                Vue d'ensemble des données du module Compétences : référentiel, fiches de
                poste, évaluations. Détecte et corrige les incohérences accumulées.
              </CardDescription>
            </div>
            <Button variant="outline" onClick={fetchData} size="sm">
              <Sparkles className="w-4 h-4 mr-2" />Actualiser
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Statut global */}
          {!legacyMigrationNeeded && !duplicatesPresent && orphanSkills === 0 ? (
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-2" data-testid="health-ok">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <p className="text-sm text-emerald-700">
                <strong>Tout est cohérent.</strong> Aucune incohérence détectée dans les données du module Compétences.
              </p>
            </div>
          ) : (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2" data-testid="health-warning">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <strong>{(legacyMigrationNeeded ? 1 : 0) + (duplicatesPresent ? 1 : 0) + (orphanSkills > 0 ? 1 : 0)} incohérence(s) détectée(s).</strong>
                {' '}Les actions de correction sont disponibles ci-dessous.
              </div>
            </div>
          )}

          {/* === FICHES DE POSTE === */}
          <div>
            <h4 className="font-semibold text-slate-900 mb-2">Fiches de poste</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Tile label="Canonique" value={jobProfiles.canonical} hint="Collection principale" color="emerald" testId="tile-canonical" />
              <Tile label="Legacy" value={jobProfiles.legacy} hint="Ancienne collection" color={jobProfiles.legacy > 0 ? 'amber' : 'slate'} testId="tile-legacy" />
              <Tile label="À migrer" value={(jobProfiles.legacyOnly || []).length} hint="Présentes uniquement en legacy" color={legacyMigrationNeeded ? 'red' : 'slate'} testId="tile-to-migrate" />
            </div>
            {(legacyMigrationNeeded || duplicatesPresent) && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {legacyMigrationNeeded && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={runConsolidate}
                    disabled={running !== null}
                    data-testid="btn-consolidate"
                  >
                    {running === 'consolidate' ? (
                      <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 mr-2" />
                    )}
                    Migrer {(jobProfiles.legacyOnly || []).length} fiche(s) legacy → canonique
                  </Button>
                )}
                {duplicatesPresent && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={runDedup}
                    disabled={running !== null}
                    data-testid="btn-dedup"
                  >
                    {running === 'dedup' ? (
                      <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5 mr-2 text-red-500" />
                    )}
                    Supprimer {(jobProfiles.duplicates || []).reduce((s, d) => s + (d.count - 1), 0)} doublon(s)
                  </Button>
                )}
              </div>
            )}
            {duplicatesPresent && (
              <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <p className="text-xs font-medium text-slate-700 mb-1">Doublons détectés :</p>
                <ul className="text-xs text-slate-600 space-y-0.5">
                  {jobProfiles.duplicates.map(d => (
                    <li key={d.name}>• <strong>{d.name}</strong> — {d.count} occurrences</li>
                  ))}
                </ul>
              </div>
            )}
            {legacyMigrationNeeded && (
              <details className="mt-3">
                <summary className="text-xs text-indigo-600 cursor-pointer">
                  Voir les {jobProfiles.legacyOnly.length} fiche(s) à migrer
                </summary>
                <ul className="text-xs text-slate-600 mt-2 space-y-0.5 max-h-40 overflow-y-auto">
                  {jobProfiles.legacyOnly.map(p => (
                    <li key={p.id}>• {p.name}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>

          {/* === COMPÉTENCES === */}
          <div>
            <h4 className="font-semibold text-slate-900 mb-2">Compétences</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Tile label="Bibliothèque" value={skills.libraryCount} hint="Compétences référencées" color="indigo" />
              <Tile label="Utilisées en évaluation" value={skills.skillsUsedInAssessments} hint="Distinctes" color="blue" />
              <Tile label="Orphelines" value={orphanSkills} hint="Évaluées mais absentes du référentiel" color={orphanSkills > 0 ? 'red' : 'slate'} />
            </div>
            {orphanSkills > 0 && (
              <p className="text-xs text-red-700 mt-2">
                {orphanSkills} compétence(s) référencée(s) dans des évaluations mais absente(s) du référentiel.
                Contactez le support pour nettoyer ces données.
              </p>
            )}
          </div>

          {/* === EMPLOYÉS === */}
          <div>
            <h4 className="font-semibold text-slate-900 mb-2">Employés</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Tile label="Total" value={employees.total} color="slate" />
              <Tile label="Actifs" value={employees.active} color="emerald" />
              <Tile label="Avec fiche de poste" value={employees.withProfile} hint={`${employees.total > 0 ? Math.round(employees.withProfile / employees.total * 100) : 0}% rattachés`} color="indigo" />
              <Tile label="Sans fiche de poste" value={employees.withoutProfile} color={employees.withoutProfile > 0 ? 'amber' : 'slate'} />
            </div>
            {employees.withoutProfile > 0 && employees.active > 0 && (
              <p className="text-xs text-slate-500 mt-2">
                Conseil : rattachez vos employés actifs à une fiche de poste pour activer la gap analysis et les recommandations de mobilité.
              </p>
            )}
          </div>

          {/* === ÉVALUATIONS === */}
          <div>
            <h4 className="font-semibold text-slate-900 mb-2">Évaluations</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Tile label="Évaluations annuelles" value={evaluations.performanceTotal} hint={`${evaluations.performanceValidated} validée(s)`} color="purple" />
              <Tile label="Auto-évaluations compétences" value={evaluations.skillAssessmentsTotal} color="indigo" />
              <Tile label="Employés évalués (compétences)" value={evaluations.distinctEmployeesAssessed} color="emerald" />
              <Tile label="Familles métiers" value={referential.families} hint={`${referential.seniorityLevels} niveaux séniorité`} color="slate" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Family Dialog
// ─────────────────────────────────────────────────────────────
function FamilyDialog({ open, onOpenChange, family, onSaved }) {
  const isEdit = Boolean(family?.id);
  const [form, setForm] = useState({
    name: '', description: '', color: '#6366f1', icon: 'briefcase',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(family ? { ...family } : { name: '', description: '', color: '#6366f1', icon: 'briefcase' });
    }
  }, [open, family]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Le nom est requis'); return;
    }
    try {
      setSaving(true);
      if (isEdit) {
        await api.put(`/career/job-families/${family.id}`, form);
        toast.success('Famille mise à jour');
      } else {
        await api.post('/career/job-families', form);
        toast.success('Famille créée');
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier la famille' : 'Nouvelle famille métier'}</DialogTitle>
          <DialogDescription>Regroupez vos métiers par grande famille (IT, Finance, RH...)</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Nom *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="family-name-input"
              placeholder="ex : Informatique & Digital"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
            />
          </div>
          <div>
            <Label>Couleur d'identité</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {FAMILY_COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm({ ...form, color: c })}
                  className={`w-9 h-9 rounded-full border-2 transition-all ${form.color === c ? 'border-slate-900 scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                  data-testid={`color-${c}`}
                />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="family-save-btn">
            {isEdit ? 'Enregistrer' : 'Créer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Business Line Dialog
// ─────────────────────────────────────────────────────────────
function BusinessLineDialog({ open, onOpenChange, businessLine, families, onSaved }) {
  const isEdit = Boolean(businessLine?.id);
  const [form, setForm] = useState({ name: '', description: '', familyId: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(businessLine ? { ...businessLine } : { name: '', description: '', familyId: families[0]?.id || '' });
    }
  }, [open, businessLine, families]);

  const handleSave = async () => {
    if (!form.name.trim() || !form.familyId) {
      toast.error('Nom et famille sont requis'); return;
    }
    try {
      setSaving(true);
      if (isEdit) {
        await api.put(`/career/business-lines/${businessLine.id}`, form);
        toast.success('Métier mis à jour');
      } else {
        await api.post('/career/business-lines', form);
        toast.success('Métier créé');
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier le métier' : 'Nouveau métier'}</DialogTitle>
          <DialogDescription>Un métier appartient à une famille (ex : "Développeur" dans "Informatique").</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Famille *</Label>
            <Select value={form.familyId} onValueChange={(v) => setForm({ ...form, familyId: v })}>
              <SelectTrigger data-testid="bl-family-select"><SelectValue placeholder="Choisir une famille" /></SelectTrigger>
              <SelectContent>
                {families.map(f => (
                  <SelectItem key={f.id} value={f.id} data-testid={`family-option-${f.id}`}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nom du métier *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="bl-name-input"
              placeholder="ex : Développeur Backend"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="bl-save-btn">
            {isEdit ? 'Enregistrer' : 'Créer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Seniority Level Dialog
// ─────────────────────────────────────────────────────────────
function SeniorityDialog({ open, onOpenChange, level, onSaved }) {
  const isEdit = Boolean(level?.id);
  const [form, setForm] = useState({ name: '', order: 1, description: '', minYearsExperience: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(level ? { ...level } : { name: '', order: 1, description: '', minYearsExperience: 0 });
    }
  }, [open, level]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Le nom est requis'); return; }
    try {
      setSaving(true);
      if (isEdit) {
        await api.put(`/career/seniority-levels/${level.id}`, form);
        toast.success('Niveau mis à jour');
      } else {
        await api.post('/career/seniority-levels', form);
        toast.success('Niveau créé');
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier le niveau' : 'Nouveau niveau de séniorité'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Nom *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="ex : Junior"
              data-testid="lvl-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Ordre (1 = junior)</Label>
              <Input
                type="number" min={0} max={20}
                value={form.order}
                onChange={(e) => setForm({ ...form, order: parseInt(e.target.value, 10) || 0 })}
                data-testid="lvl-order-input"
              />
            </div>
            <div>
              <Label>Années min. d'expérience</Label>
              <Input
                type="number" min={0} max={50}
                value={form.minYearsExperience}
                onChange={(e) => setForm({ ...form, minYearsExperience: parseInt(e.target.value, 10) || 0 })}
                data-testid="lvl-years-input"
              />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="lvl-save-btn">
            {isEdit ? 'Enregistrer' : 'Créer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────
export default function CareerReferentialPage() {
  const { user } = useAuth();
  const companyId = user?.companyId;
  const [families, setFamilies] = useState([]);
  const [jobProfiles, setJobProfiles] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const [familyDialog, setFamilyDialog] = useState({ open: false, family: null });
  const [lvlDialog, setLvlDialog] = useState({ open: false, level: null });
  const [assignFamilyFor, setAssignFamilyFor] = useState(null); // profile being reassigned

  const fetchAll = useCallback(async () => {
    try {
      const [fRes, pRes, lRes] = await Promise.all([
        api.get('/career/job-families'),
        api.get(`/skills/job-profiles?company_id=${companyId}`),
        api.get('/career/seniority-levels'),
      ]);
      setFamilies(fRes.data?.items || []);
      // /skills/job-profiles returns { jobProfiles: [...] } or { profiles: [...] } selon les versions
      const profiles = pRes.data?.jobProfiles || pRes.data?.profiles || pRes.data?.items || [];
      setJobProfiles(profiles);
      setLevels(lRes.data?.items || []);
    } catch (e) {
      toast.error('Erreur de chargement du référentiel');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { if (companyId) fetchAll(); }, [companyId, fetchAll]);

  // Assign / detach a job profile to a family
  const assignToFamily = async (profileId, familyId) => {
    try {
      await api.put(`/skills/job-profiles/${profileId}?company_id=${companyId}`, { familyId: familyId || null });
      toast.success(familyId ? 'Métier rattaché à la famille' : 'Famille retirée');
      setAssignFamilyFor(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const handleSeed = async () => {
    if (!window.confirm("Créer les familles métiers et niveaux par défaut ? (sans doublons)")) return;
    try {
      setSeeding(true);
      const res = await api.post('/career/seed-defaults');
      toast.success(`${res.data.createdFamilies} familles + ${res.data.createdLevels} niveaux créés`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSeeding(false);
    }
  };

  const deleteFamily = async (id) => {
    if (!window.confirm('Supprimer cette famille ?')) return;
    try {
      await api.delete(`/career/job-families/${id}`);
      toast.success('Famille supprimée');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };
  const deleteLvl = async (id) => {
    if (!window.confirm('Supprimer ce niveau ?')) return;
    try {
      await api.delete(`/career/seniority-levels/${id}`);
      toast.success('Niveau supprimé');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const familyById = (id) => families.find(f => f.id === id);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="career-referential-page">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-indigo-600" />
              Référentiel métiers & carrières
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Familles métiers, métiers et niveaux de séniorité — socle de votre GPEC
            </p>
          </div>
          {(families.length === 0 || levels.length === 0) && (
            <Button
              onClick={handleSeed}
              disabled={seeding}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="seed-defaults-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Créer le référentiel par défaut
            </Button>
          )}
        </div>

        <Tabs defaultValue="families" className="space-y-4">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="families" data-testid="tab-families">
              <Layers className="w-4 h-4 mr-2" />Familles ({families.length})
            </TabsTrigger>
            <TabsTrigger value="business-lines" data-testid="tab-business-lines">
              <Briefcase className="w-4 h-4 mr-2" />Métiers ({jobProfiles.length})
            </TabsTrigger>
            <TabsTrigger value="seniority" data-testid="tab-seniority">
              <TrendingUp className="w-4 h-4 mr-2" />Niveaux ({levels.length})
            </TabsTrigger>
            <TabsTrigger value="health" data-testid="tab-health">
              <ShieldCheck className="w-4 h-4 mr-2" />Cohérence
            </TabsTrigger>
          </TabsList>

          {/* === FAMILLES === */}
          <TabsContent value="families">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Familles métiers</CardTitle>
                  <CardDescription>Grandes catégories regroupant vos métiers</CardDescription>
                </div>
                <Button
                  onClick={() => setFamilyDialog({ open: true, family: null })}
                  data-testid="new-family-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />Nouvelle famille
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-sm text-slate-400 text-center py-8">Chargement...</p>
                ) : families.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">Aucune famille — créez-en une ou utilisez le référentiel par défaut</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {families.map(f => (
                      <Card key={f.id} className="hover:shadow-md transition-shadow" data-testid={`family-${f.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: f.color }}
                              />
                              <span className="font-medium text-slate-900 truncate">{f.name}</span>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => setFamilyDialog({ open: true, family: f })}
                                data-testid={`edit-family-${f.id}`}
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => deleteFamily(f.id)}
                                data-testid={`delete-family-${f.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                              </Button>
                            </div>
                          </div>
                          {f.description && (
                            <p className="text-xs text-slate-500 mt-2 line-clamp-2">{f.description}</p>
                          )}
                          <div className="flex gap-2 mt-3">
                            <Badge variant="outline" className="text-xs">
                              {f.businessLineCount || 0} métiers
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {f.jobProfileCount || 0} postes
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* === MÉTIERS = FICHES DE POSTE === */}
          <TabsContent value="business-lines">
            <Card>
              <CardHeader>
                <CardTitle>Métiers de l'entreprise</CardTitle>
                <CardDescription>
                  Vos fiches de poste existantes, regroupées par famille. Les compétences requises de chaque
                  métier proviennent directement de la fiche de poste correspondante. Cliquez sur un métier
                  pour le rattacher à une famille.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-sm text-slate-400 text-center py-8">Chargement...</p>
                ) : jobProfiles.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">
                      Aucune fiche de poste — créez-en depuis Talents → Fiches de poste
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Grouper par famille (+ "Non rattachés") */}
                    {(() => {
                      const groups = {};
                      // Une entrée par famille existante (même vide → on l'affichera si profile existe)
                      jobProfiles.forEach(p => {
                        const key = p.familyId || '__unassigned__';
                        if (!groups[key]) groups[key] = [];
                        groups[key].push(p);
                      });
                      const entries = Object.entries(groups).sort(([a], [b]) => {
                        if (a === '__unassigned__') return 1;
                        if (b === '__unassigned__') return -1;
                        const fa = familyById(a)?.name || '';
                        const fb = familyById(b)?.name || '';
                        return fa.localeCompare(fb, 'fr');
                      });
                      return entries.map(([key, profiles]) => {
                        const fam = key === '__unassigned__' ? null : familyById(key);
                        return (
                          <div key={key} data-testid={`group-${key}`}>
                            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-200">
                              <span
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: fam?.color || '#94a3b8' }}
                              />
                              <h3 className="font-semibold text-slate-900">
                                {fam?.name || 'Non rattachés à une famille'}
                              </h3>
                              <Badge variant="outline" className="text-xs ml-1">
                                {profiles.length} métier{profiles.length > 1 ? 's' : ''}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {profiles.map(p => {
                                const skills = p.skills || [];
                                const skillsCount = p.skillsCount ?? skills.length;
                                const criticalCount = p.criticalSkillsCount
                                  ?? skills.filter(s => s.importance === 'critical').length;
                                const employeeCount = p.employeeCount ?? 0;
                                const visibleSkills = skills.slice(0, 4);
                                const moreCount = skills.length - visibleSkills.length;
                                return (
                                  <Card
                                    key={p.id}
                                    className="hover:shadow-md transition-shadow"
                                    data-testid={`profile-${p.id}`}
                                  >
                                    <CardContent className="p-4">
                                      <div className="flex items-start justify-between gap-2 mb-2">
                                        <div className="min-w-0">
                                          <p className="font-medium text-slate-900 truncate">{p.name}</p>
                                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                                            <Badge variant="outline" className="text-xs">
                                              <Users className="w-3 h-3 mr-1" />
                                              {employeeCount} employé(s)
                                            </Badge>
                                            <Badge variant="outline" className="text-xs">
                                              <Award className="w-3 h-3 mr-1" />
                                              {skillsCount} compétence{skillsCount > 1 ? 's' : ''}
                                            </Badge>
                                            {criticalCount > 0 && (
                                              <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-200">
                                                {criticalCount} critique{criticalCount > 1 ? 's' : ''}
                                              </Badge>
                                            )}
                                          </div>
                                        </div>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => setAssignFamilyFor(p)}
                                          data-testid={`assign-family-${p.id}`}
                                        >
                                          <Tag className="w-3.5 h-3.5 mr-1" />
                                          Famille
                                        </Button>
                                      </div>
                                      {/* Compétences visibles */}
                                      {skills.length > 0 ? (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                          {visibleSkills.map((s, idx) => {
                                            const label = s.skillName || s.skill?.name || s.name || '—';
                                            return (
                                              <Badge
                                                key={idx}
                                                variant="outline"
                                                className={`text-xs ${
                                                  s.importance === 'critical'
                                                    ? 'bg-red-50 text-red-700 border-red-200'
                                                    : s.importance === 'important'
                                                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                                                    : 'bg-slate-50 text-slate-700 border-slate-200'
                                                }`}
                                                title={`Niveau requis : ${s.requiredLevel || '?'}`}
                                              >
                                                {label}
                                                {s.requiredLevel && (
                                                  <span className="ml-1 opacity-60">N{s.requiredLevel}</span>
                                                )}
                                              </Badge>
                                            );
                                          })}
                                          {moreCount > 0 && (
                                            <Badge variant="outline" className="text-xs text-slate-500">
                                              +{moreCount} autre{moreCount > 1 ? 's' : ''}
                                            </Badge>
                                          )}
                                        </div>
                                      ) : (
                                        <p className="text-xs text-slate-400 mt-2">
                                          Aucune compétence définie sur cette fiche de poste
                                        </p>
                                      )}
                                    </CardContent>
                                  </Card>
                                );
                              })}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* === NIVEAUX === */}
          <TabsContent value="seniority">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Niveaux de séniorité</CardTitle>
                  <CardDescription>Échelle de progression (Junior → Senior → Lead)</CardDescription>
                </div>
                <Button
                  onClick={() => setLvlDialog({ open: true, level: null })}
                  data-testid="new-lvl-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />Nouveau niveau
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-sm text-slate-400 text-center py-8">Chargement...</p>
                ) : levels.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">Aucun niveau — créez-en un ou utilisez le référentiel par défaut</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {levels.map(lvl => (
                      <div
                        key={lvl.id}
                        className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50"
                        data-testid={`lvl-${lvl.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="font-bold">{lvl.order}</Badge>
                          <div>
                            <p className="font-medium text-slate-900">{lvl.name}</p>
                            <p className="text-xs text-slate-500">
                              {lvl.minYearsExperience}+ ans · {lvl.description || '—'}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setLvlDialog({ open: true, level: lvl })}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteLvl(lvl.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* === COHÉRENCE / DIAGNOSTIC === */}
          <TabsContent value="health">
            <DataHealthPanel />
          </TabsContent>
        </Tabs>

        {/* Dialogs */}
        <FamilyDialog
          open={familyDialog.open}
          onOpenChange={(o) => setFamilyDialog({ ...familyDialog, open: o })}
          family={familyDialog.family}
          onSaved={fetchAll}
        />
        {/* Assign-to-family dialog (pour les fiches de poste) */}
        <Dialog open={!!assignFamilyFor} onOpenChange={(o) => !o && setAssignFamilyFor(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Rattacher à une famille</DialogTitle>
              <DialogDescription>
                {assignFamilyFor?.name} — choisissez la famille métier de rattachement
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1 max-h-72 overflow-y-auto py-2">
              <button
                type="button"
                onClick={() => assignToFamily(assignFamilyFor.id, null)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors text-left ${!assignFamilyFor?.familyId ? 'bg-slate-100 text-slate-900' : 'hover:bg-slate-50 text-slate-600'}`}
                data-testid="assign-none"
              >
                <span className="w-3 h-3 rounded-full bg-slate-300" />
                <span className="flex-1">Aucune famille</span>
                {!assignFamilyFor?.familyId && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
              </button>
              {families.map(f => {
                const isCurrent = assignFamilyFor?.familyId === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => assignToFamily(assignFamilyFor.id, f.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors text-left ${isCurrent ? 'bg-indigo-50 text-indigo-900' : 'hover:bg-slate-50 text-slate-700'}`}
                    data-testid={`assign-family-${f.id}`}
                  >
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: f.color }} />
                    <span className="flex-1 font-medium">{f.name}</span>
                    {isCurrent && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                  </button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
        <SeniorityDialog
          open={lvlDialog.open}
          onOpenChange={(o) => setLvlDialog({ ...lvlDialog, open: o })}
          level={lvlDialog.level}
          onSaved={fetchAll}
        />
      </div>
    </DashboardLayout>
  );
}
